import { useEffect, useState } from 'react';
import {
  Button, Card, Dialog, DialogPanel,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { Link } from 'react-router-dom';
import type { StaffAttribution, StaffRow, StaffTopPart, StaffWindow } from '../api/types';
import { fetchStaffTopParts } from '../api/salespeople';
import { t } from '../lib/i18n';

const mxn = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

interface Props {
  open: boolean;
  row: StaffRow | null;
  attribution: StaffAttribution;
  win: StaffWindow;
  onClose: () => void;
}

export default function StaffTopPartsModal({ open, row, attribution, win, onClose }: Props) {
  const [parts, setParts] = useState<StaffTopPart[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    fetchStaffTopParts({ attribution, id: row.staff_id, win })
      .then((p) => { if (alive) setParts(p); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, row, attribution, win]);

  if (!row) return null;

  const salesQuery = attribution === 'slid' ? `slid=${row.staff_id}` : `ctrmn_id=${row.staff_id}`;

  return (
    <Dialog open={open} onClose={onClose} static={true}>
      <DialogPanel>
        <Title>{`${t.salespeople.modal.title} — ${row.name}`}</Title>
        {loading && <Text className="mt-4">…</Text>}
        {err && <Text className="mt-4 text-red-600 dark:text-red-400">{err}</Text>}
        {!loading && !err && parts.length === 0 && (
          <Text className="mt-4">{t.salespeople.modal.empty}</Text>
        )}
        {!loading && parts.length > 0 && (
          <Card className="mt-4 p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.tables.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.tables.cols.description}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.tables.cols.qty}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.tables.cols.revenue}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.customer.topParts.cols.margin}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parts.map((p) => (
                  <TableRow key={p.part}>
                    <TableCell className="font-mono text-xs">
                      {/* These rows are salesperson-attributed and carry no
                          store, so per the JCD-59 rule they keep the part-page
                          link rather than drilling an inv record. A row without
                          a line (mid-deploy window: new SPA, old daemon) renders
                          plain text — the old /parts?q= fallback was a substring
                          search that answered a different question. */}
                      {p.line && p.part ? (
                        <Link
                          to={`/parts/${encodeURIComponent(p.line)}/${encodeURIComponent(p.part)}`}
                          className="text-tremor-brand hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {p.part}
                        </Link>
                      ) : (
                        p.part
                      )}
                    </TableCell>
                    <TableCell>{p.pdesc}</TableCell>
                    <TableCell className="text-right">{p.qty_sold}</TableCell>
                    <TableCell className="text-right">{mxn(p.revenue_mxn)}</TableCell>
                    <TableCell className="text-right">{mxn(p.margin_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Link to={`/sales?${salesQuery}`}>
            <Button variant="secondary">{t.salespeople.modal.seeTickets}</Button>
          </Link>
          <Button onClick={onClose}>{t.common.close}</Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
